<script setup>
import { router, usePage } from '@inertiajs/vue3';
import { reactive } from 'vue';
import { ref } from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css';

// includes we need to front end locale magic
import { useI18n } from 'vue-i18n';

// Get the Programme, Organisation, and props we need to get the user preferred locale
const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const usePageProps = usePage().props;

// Get locale as set in cookie
const { locale, fallbackLocale} = useI18n({ useScope: 'global' });

// Set up all the data variables we are going to initially need
const props = defineProps({
    auditorTeamQuestion: Object,
    formData: Object,
    key: Number,
    datePicker: Object,
    adminAudit: Boolean
});

const convertToObject = (json) => {
    if (json.constructor.name != 'Object') {
        json = JSON.parse(json)
    }
    return json;
}

const auditorTeamQuestion = props.auditorTeamQuestion;
const auditorTeamQuestionKey = auditorTeamQuestion.index_id;
const auditorTeamQuestionFieldType = auditorTeamQuestion.type;

// If the field type is 'select', set up appropriate variables and build the array of options
let selectListObject = '';
let selectListObjectKeys = [];
let selectList = [];
let selectOption = [];
let selectOptions = [];
if (auditorTeamQuestionFieldType == 'select') {
    selectListObject = props.auditorTeamQuestion.select_options;

    if (selectListObject.constructor.name != 'Object') {
        selectListObject = JSON.parse(selectListObject);
    }

    selectListObjectKeys = Object.keys(selectListObject);

    if ( selectListObjectKeys.includes(locale.value) ) {
        selectList = selectListObject[locale.value];
    }
    else {
        selectList = selectListObject[fallbackLocale.value];
    }

    const selectOptionNames = selectList.split(",");
    const selectOptionValues = selectList.split(",");

    for (var i = 0; i < selectOptionNames.length; i++) {
        selectOption = {};
        selectOption.name = selectOptionNames[i].trim();
        selectOption.value = selectOptionValues[i].trim();
        selectOptions.push(selectOption);
    }
}

const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
let date = ref(new Date());
if (auditorTeamQuestionFieldType == 'date') {
   date = props.formData[auditorTeamQuestionKey].answer;
}
const format = (date) => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `Selected date is ${day}/${month}/${year}`;
}

const auditorWYSIWYGForm = reactive({
    auditorWYSIWYGField: '',
    auditorWYSIWYGDraftID: '',
    auditorWYSIWYGDraftLastSaved: '',
});

if (auditorTeamQuestionFieldType == 'wysiwyg') {
    const auditorWYSIWYGEditor = ref('auditorWYSIWYGEditor');
}

const auditorTeamQuestionLabel = auditorTeamQuestion.label;
const auditorTeamQuestionID = auditorTeamQuestion.id;
const auditorTeamQuestionUniqueID = 'auditorTeamQuestion-' + auditorTeamQuestionID;

</script>

<template>

    <div class="mt-4">

        <label class="block mb-2" :for="auditorTeamQuestionUniqueID">
            <span v-if="convertToObject(props.auditorTeamQuestion.label)[$i18n.locale] || convertToObject(props.auditorTeamQuestion.label)[$i18n.fallbackLocale]" v-html="convertToObject(props.auditorTeamQuestion.label)[$i18n.locale] ? convertToObject(props.auditorTeamQuestion.label)[$i18n.locale] : convertToObject(props.auditorTeamQuestion.label)[$i18n.fallbackLocale]" class=""></span>
            <span v-if="auditorTeamQuestion.required == true" class="required-form-field">*</span>
        </label>

        <div v-if="auditorTeamQuestionFieldType == 'text'" class="form-control">
            <input
            v-model="formData[auditorTeamQuestionKey].answer"
            type="text"
            :name="auditorTeamQuestionID"
            :id="auditorTeamQuestionUniqueID"
            :required="auditorTeamQuestion.required == true"
            :disabled="adminAudit"
            class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
        </div>

        <div v-else-if="auditorTeamQuestionFieldType == 'wysiwyg'" class="form-control">
            <QuillEditor
                v-model:content="formData[auditorTeamQuestionKey].answer"
                :name="auditorTeamQuestionID"
                :id="auditorTeamQuestionUniqueID"
                :required="auditorTeamQuestion.required == true"
                ref="auditorWYSIWYGEditor"
                ctheme="snow"
                contentType="html"
                :readOnly="adminAudit"
            />
        </div>

        <div v-else-if="auditorTeamQuestionFieldType == 'textarea'" class="form-control">
            <textarea
            v-model="formData[auditorTeamQuestionKey].answer"
            :name="auditorTeamQuestionID"
            :id="auditorTeamQuestionUniqueID"
            :required="auditorTeamQuestion.required == true"
            :disabled="adminAudit"
            class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            ></textarea>
        </div>

        <div v-else-if="auditorTeamQuestionFieldType == 'select'" class="form-control">
            <select
            v-if="selectOptions"
            v-model="formData[auditorTeamQuestionKey].answer"
            :name="auditorTeamQuestionID"
            :id="auditorTeamQuestionUniqueID"
            :required="auditorTeamQuestion.required == true"
            :disabled="adminAudit"
            class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            >
            <option value="">{{ $t('teams.questions.option') }}</option>
            <option v-for="optionItem in selectOptions"
                :name="optionItem.name"
                :value="optionItem.value"
            >{{optionItem.name}}</option>
            </select>
        </div>

        <div v-else-if="auditorTeamQuestionFieldType == 'checkbox'" class="form-control">
            <input
            type="checkbox"
            v-model="formData[auditorTeamQuestionKey].answer"
            :name="auditorTeamQuestionID"
            :id="auditorTeamQuestionUniqueID"
            :required="auditorTeamQuestion.required == true"
            :disabled="adminAudit"
            class="mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
            />
        </div>

        <div v-else-if="auditorTeamQuestionFieldType == 'date'" class="form-control">
            <div>
                <Datepicker
                v-model="formData[auditorTeamQuestionKey].answer"
                :format="format"
                :name="auditorTeamQuestionID"
                :id="auditorTeamQuestionUniqueID"
                :required="auditorTeamQuestion.required == true"
                :disabled="adminAudit"
                />
            </div>

        </div>

    </div>

</template>
